import CoachOptions from '../WorkoutDebrief/CoachOptions';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDurationMin } from '../../utils/duration';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function findCurrentWeekIndex(weeks, todayDateISO) {
  if (!Array.isArray(weeks) || weeks.length === 0) return 0;
  if (!todayDateISO) return 0;

  // Prefer the week that contains today
  const idx = weeks.findIndex((w) =>
    (w?.workouts || []).some((d) => d?.date === todayDateISO)
  );
  if (idx >= 0) return idx;

  // Otherwise: first week
  return 0;
}

function toIsoDate(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function addDaysIso(iso, days) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function getWeekMondayIso(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  const offset = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
}

function findWeeklyAnalysisForWeek(weeklyAnalyses, week) {
  const dates = (week?.workouts || []).map((w) => toIsoDate(w?.date)).filter(Boolean).sort();
  const weekStart = getWeekMondayIso(dates[0] || '');
  if (!weekStart) return null;
  const weekEnd = addDaysIso(weekStart, 6);
  return (weeklyAnalyses || []).find((item) =>
    toIsoDate(item?.week_start) === weekStart ||
    (toIsoDate(item?.week_start) <= weekStart && toIsoDate(item?.week_end) >= weekEnd)
  ) || null;
}

function StatRow({ label, plannedValue, actualValue, unit, type = 'number' }) {
  const plannedNum = Number(plannedValue || 0);
  const actualNum = Number(actualValue || 0);
  const hasPlanned = Number.isFinite(plannedNum) && plannedNum > 0;
  const hasActual = Number.isFinite(actualNum) && actualNum > 0;
  const ratioRaw = hasPlanned ? (actualNum / plannedNum) : 0;
  const pctRaw = hasPlanned ? Math.round(ratioRaw * 100) : 0;
  const pctFill = hasPlanned ? Math.min(100, Math.max(0, pctRaw)) : 0;
  const overflow = hasPlanned && pctRaw > 100;

  const actualText = hasActual
    ? (type === 'duration'
        ? (formatDurationMin(actualNum) ?? '—')
        : `${Math.round(actualNum)}${unit}`)
    : '—';

  return (
    <div className="border border-gray-200 rounded-xl p-2 bg-white">
      <div className="text-[10px] font-extrabold text-gray-500 uppercase">{label}</div>
      <div className="mt-0.5 text-[12px] font-extrabold tabular-nums text-gray-900">
        {actualText}
      </div>

      <div className="mt-2 relative h-2 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
        <div
          className={`h-full ${hasPlanned ? 'bg-indigo-600' : 'bg-gray-300'}`}
          style={{ width: `${pctFill}%` }}
          title={hasPlanned ? `${pctRaw}%` : ''}
        />
        {overflow ? (
          <span
            className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-orange-500"
            title={`${pctRaw}%`}
          />
        ) : null}
      </div>
      {hasPlanned ? <div className="mt-1 text-[10px] text-gray-500 font-bold">{pctRaw}%</div> : null}
    </div>
  );
}

export default function MobilePlanTopCarousel({
  isBike,
  weeks,
  todayDate,
  getWeekPlannedStats,
  getWeekActualStats,
  weeklyAnalyses = [],
  planId = null,
  canEdit = false,
  onWeeklyOptionApplied = null,
  plan = null
}) {
  const { t } = useTranslation();

  const initialWeekIdx = useMemo(() => findCurrentWeekIndex(weeks, todayDate), [weeks, todayDate]);
  const [weekIdx, setWeekIdx] = useState(initialWeekIdx);

  useEffect(() => {
    setWeekIdx(initialWeekIdx);
  }, [initialWeekIdx]);

  const safeWeekIdx = clamp(weekIdx, 0, Math.max(0, (weeks?.length || 1) - 1));
  const week = Array.isArray(weeks) ? weeks[safeWeekIdx] : null;

  const planned = week ? getWeekPlannedStats(week.workouts || []) : { km: 0, elev: 0, min: 0 };
  const actual = week ? getWeekActualStats(week.workouts || []) : { km: 0, elev: 0, min: 0 };
  const weeklyAnalysis = useMemo(
    () => findWeeklyAnalysisForWeek(weeklyAnalyses, week),
    [weeklyAnalyses, week]
  );
  const analysis = weeklyAnalysis?.analysis_json || null;
  const snapshot = weeklyAnalysis?.snapshot_json || null;

  const hasPrevWeek = safeWeekIdx > 0;
  const hasNextWeek = safeWeekIdx < (weeks?.length || 1) - 1;

  return (
    <div className="mb-6">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="border-b border-gray-100">
          <div className="flex items-center justify-between px-3 py-2 gap-2">
            <button
              type="button"
              onClick={() => setWeekIdx((i) => clamp(i - 1, 0, (weeks?.length || 1) - 1))}
              disabled={!hasPrevWeek}
              className={`w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center ${
                !hasPrevWeek ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 cursor-pointer'
              }`}
              aria-label={t('workout.previous')}
              title={t('workout.previous')}
            >
              ‹
            </button>

            <div className="min-w-0 flex-1 text-center">
              <div className="text-sm font-extrabold text-gray-900">
                {t('plan.weekly_stats')} · {t('plan.week')} {week?.week_number ?? (safeWeekIdx + 1)}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setWeekIdx((i) => clamp(i + 1, 0, (weeks?.length || 1) - 1))}
              disabled={!hasNextWeek}
              className={`w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center ${
                !hasNextWeek ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 cursor-pointer'
              }`}
              aria-label={t('workout.next')}
              title={t('workout.next')}
            >
              ›
            </button>
          </div>

          {(week?.focus || week?.phase) ? (
            <div className="px-4 pb-2 text-sm font-semibold text-gray-700 leading-snug">
              {week?.focus || week?.phase || ''}
            </div>
          ) : null}
        </div>

        <div className="px-3 py-3">
          <div>
            <div className="grid grid-cols-3 gap-2">
              <StatRow
                label={t('plan.distance')}
                plannedValue={planned.km || 0}
                actualValue={actual.km || 0}
                unit=" km"
              />
              <StatRow
                label={t('plan.duration')}
                plannedValue={planned.min || 0}
                actualValue={actual.min || 0}
                unit=" min"
                type="duration"
              />
              <StatRow
                label={t('plan.elevation')}
                plannedValue={planned.elev || 0}
                actualValue={actual.elev || 0}
                unit=" m"
              />
            </div>
            {weeklyAnalysis ? (
              <div className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50/60 p-3">
                <div className="text-[10px] font-extrabold uppercase tracking-wide text-indigo-600 mb-1">
                  {t('plan.weekly_analysis.title')}
                </div>
                {analysis?.headline ? (
                  <div className="text-sm font-extrabold text-gray-900 leading-snug">
                    {analysis.headline}
                  </div>
                ) : (
                  <div className="text-sm font-extrabold text-gray-900 leading-snug">
                    {t('plan.weekly_analysis.snapshot_ready')}
                  </div>
                )}
                {analysis?.recap ? (
                  <div className="text-xs font-medium text-gray-700 mt-1 leading-relaxed">
                    {analysis.recap}
                  </div>
                ) : null}
                {analysis?.summary?.next_week_focus ? (
                  <div className="text-xs font-semibold text-indigo-800 mt-2 leading-relaxed">
                    {analysis.summary.next_week_focus}
                  </div>
                ) : snapshot?.nextHighlights?.length ? (
                  <div className="text-xs font-semibold text-indigo-800 mt-2 leading-relaxed">
                    {t('plan.weekly_analysis.next_up')}: {snapshot.nextHighlights.join(' · ')}
                  </div>
                ) : null}
                {Array.isArray(analysis?.options) && analysis.options.length > 0 ? (
                  <div className="mt-3">
                    <div className="text-[10px] font-extrabold uppercase tracking-wide text-indigo-600 mb-1">
                      {t('plan.weekly_analysis.next_week_options')}
                    </div>
                    <CoachOptions
                      analysis={{ ...analysis, coach_read: null }}
                      decision={analysis.decision || null}
                      planId={planId}
                      source="weekly"
                      sourceId={weeklyAnalysis.id}
                      canEdit={canEdit}
                      plan={plan}
                      onApplied={onWeeklyOptionApplied}
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs font-medium text-gray-500">
                {t('plan.weekly_analysis.empty')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


